import { useSyncExternalStore } from 'react'

const KEY = 'signupChooserShown'

type DecisionProfile = {
  id?: number
  taxEntityType?: string | null
  practiceType?: string | null
}

const scopeOf = (profile: DecisionProfile | undefined): string | null =>
  profile?.id === undefined
    ? null
    : `${profile.id}:${profile.taxEntityType}:${profile.practiceType}`

const listeners = new Set<() => void>()

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/**
 * The routing taken at the practice step or shown by the chooser, tied to the profile and answers it
 * was made for, so Back and the header follow the route taken and another signup's decision is never
 * inherited.
 */
export const recordChooserDecision = (
  profile: DecisionProfile | undefined,
  shown: boolean
): void => {
  const scope = scopeOf(profile)
  if (!scope || readChooserDecision(profile) === shown) return
  localStorage.setItem(KEY, JSON.stringify({ scope, shown }))
  listeners.forEach((listener) => listener())
}

/** Null when nothing was recorded for this profile and these answers. */
export const readChooserDecision = (
  profile: DecisionProfile | undefined
): boolean | null => {
  const scope = scopeOf(profile)
  const stored = localStorage.getItem(KEY)
  if (!scope || stored === null) return null
  try {
    const parsed: unknown = JSON.parse(stored)
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      'scope' in parsed &&
      'shown' in parsed &&
      parsed.scope === scope &&
      typeof parsed.shown === 'boolean'
    ) {
      return parsed.shown
    }
  } catch {
    return null
  }
  return null
}

/** Like `readChooserDecision`, and re-renders when a decision is recorded. */
export const useChooserDecision = (
  profile: DecisionProfile | undefined
): boolean | null =>
  useSyncExternalStore(subscribe, () => readChooserDecision(profile))
